import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { ConnectedCarStatusPayload, DynamicConnectedCarPayload, FleetVehicleAction, FleetVehicleHistoryAction } from 'types/kong'
import { KongState, TicketPayload } from 'types/redux'
import { SmartAssignment } from 'types/entegral'
import { Participant } from 'types/global'
import { LocationPayload } from 'types/location'
import { getInitialKongState } from 'redux/helpers/kongHelpers'
import { mergeWith, uniqBy } from 'lodash'
import { TransformedTicket } from 'types/ticket'

const initialState: KongState = getInitialKongState()

const kongSlice = createSlice({
  name: 'Kong',
  initialState,
  reducers: {
    // Connected Car
    setConnectedVehicleState: (state: KongState, action: PayloadAction<ConnectedCarStatusPayload>) => {
      state.connectedCar.connectedVehicles = uniqBy([...state.connectedCar.connectedVehicles, action.payload], 'vin')
      return state
    },
    setConnectedState: (state: KongState, action: PayloadAction<DynamicConnectedCarPayload>) => {
      state.connectedCar = mergeWith({}, state.connectedCar, action.payload)
      return state
    },
    setLastVinSearched: (state: KongState, action: PayloadAction<string | null>) => {
      state.connectedCar.lastVinSearched = action.payload
      return state
    },
    resetConnectedSearchState: (state: KongState) => {
      state.connectedCar = initialState.connectedCar
      return state
    },
    // Entegral
    setEntegralAssignment: (state: KongState, action: PayloadAction<SmartAssignment | null>) => {
      state.entegral.assignment = action.payload
    },
    setEntegralParticipant: (state: KongState, action: PayloadAction<Participant | null>) => {
      state.entegral.participant = action.payload
    },
    setEntegralCustomDropoffDestination: (state: KongState, action: PayloadAction<LocationPayload | null>) => {
      state.entegral.customDropoffDestination = action.payload
    },
    resetEntegralState: (state: KongState) => {
      state.entegral = getInitialKongState().entegral
      return state
    },

    // Exchange
    setExchangeSearch: (state: KongState, action: PayloadAction<KongState['exchange']>) => {
      state.exchange.data = action.payload.data
    },
    resetExchangeSearchState: (state: KongState) => {
      state.exchange = getInitialKongState().exchange
      return state
    },

    // Fleet Vehicle
    setFleetVehicle: (state: KongState, action: PayloadAction<FleetVehicleAction>) => {
      state.fleetVehicle.vehicle = action.payload.vehicle
    },
    setFleetVehicleHistory: (state: KongState, action: PayloadAction<FleetVehicleHistoryAction>) => {
      state.fleetVehicle.vehicleHistory = [...state.fleetVehicle.vehicleHistory, ...action.payload.vehicleHistory]
    },
    resetFleetVehicleState: (state: KongState) => {
      state.fleetVehicle = getInitialKongState().fleetVehicle
      return state
    },

    // MultiRetireve
    updateTicket: (state: KongState, action: PayloadAction<TicketPayload>) => {
      state.multiRetrieve.ticket = action.payload.ticket
    },
    updateTicketVehicle: (state: KongState, action: PayloadAction<{ vehicle: TransformedTicket['vehicle'] }>) => {
      if (state.multiRetrieve.ticket) {
        state.multiRetrieve.ticket.vehicle = action.payload.vehicle
      }
      return state
    },
    updateTicketError: (state: KongState, action: PayloadAction<{ error?: any }>) => {
      state.multiRetrieve.ticketError = action.payload.error
    },
    resetTicket: (state: KongState, action: PayloadAction<{}>) => {
      state.multiRetrieve = getInitialKongState().multiRetrieve
      return state
    },
  },
})

export const {
  setConnectedVehicleState,
  setConnectedState,
  setLastVinSearched,
  resetConnectedSearchState,
  setEntegralAssignment,
  setEntegralParticipant,
  setEntegralCustomDropoffDestination,
  resetEntegralState,
  setExchangeSearch,
  resetExchangeSearchState,
  setFleetVehicle,
  setFleetVehicleHistory,
  resetFleetVehicleState,
  updateTicket,
  updateTicketVehicle,
  updateTicketError,
  resetTicket,
} = kongSlice.actions

export default kongSlice.reducer
